/**
 * @typedef {"administrative_area_level_1" |
             "administrative_area_level_2" | "administrative_area_level_3" |
             "administrative_area_level_4" | "administrative_area_level_5" |
             "administrative_area_level_6" | "administrative_area_level_7" |
             "archipelago" | "colloquial_area" | "continent" | "country" |
             "establishment" | "finance" | "floor" | "food" |
             "general_contractor" | "geocode" | "health" | "intersection" |
             "landmark" | "locality" | "natural_feature" | "neighborhood" |
             "place_of_worship" | "plus_code" | "point_of_interest" |
             "political" | "post_box" | "postal_code" | "postal_code_prefix" |
             "postal_code_suffix" | "postal_town" | "premise" | "room" |
             "route" | "street_address" | "street_number" | "sublocality" |
             "sublocality_level_1" | "sublocality_level_2" |
             "sublocality_level_3" | "sublocality_level_4" |
             "sublocality_level_5" | "subpremise" | "town_square"} AddressComponentType
 */

/**
 * @typedef {object} AddressComponent
 * @property {string} long_name
 * @property {string} short_name
 * @property {AddressComponentType[]} types
 */

/**
 * Extract address items from a google place info `address_components` field.
 * (See https://developers.google.com/maps/documentation/places/web-service/details#Place).
 *
 * Returns null if the value is not present in `result`.
 *
 * @param {AddressComponent[]} addressComponents
 * @param {AddressComponentType} type
 * @returns {string | null}
 */
function getValueFromAddressComponents(addressComponents, type) {
  const obj = addressComponents.find((component) =>
    component.types.includes(type)
  );
  return obj?.long_name ?? null;
}

/**
 * @typedef {Object} Place https://developers.google.com/maps/documentation/places/web-service/details#Place (converted with ChatGPT, may have minor errors)
 * @property {Array<AddressComponent>} [address_components] - An array containing the separate components applicable to this address.
 * @property {string} [adr_address] - A representation of the place's address in the adr microformat.
 * @property {string} [business_status] - Indicates the operational status of the place, if it is a business.
 * @property {boolean} [curbside_pickup] - Specifies if the business supports curbside pickup.
 * @property {PlaceOpeningHours} [current_opening_hours] - Contains the hours of operation for the next seven days.
 * @property {boolean} [delivery] - Specifies if the business supports delivery.
 * @property {boolean} [dine_in] - Specifies if the business supports indoor or outdoor seating options.
 * @property {PlaceEditorialSummary} [editorial_summary] - Contains a summary of the place.
 * @property {string} [formatted_address] - A string containing the human-readable address of this place.
 * @property {string} [formatted_phone_number] - Contains the place's phone number in its local format.
 * @property {Geometry} [geometry] - Contains the location and viewport for the location.
 * @property {string} [icon] - Contains the URL of a suggested icon which may be displayed to the user.
 * @property {string} [icon_background_color] - Contains the default HEX color code for the place's category.
 * @property {string} [icon_mask_base_uri] - Contains the URL of a recommended icon.
 * @property {string} [international_phone_number] - Contains the place's phone number in international format.
 * @property {string} [name] - Contains the human-readable name for the returned result.
 * @property {PlaceOpeningHours} [opening_hours] - Contains the regular hours of operation.
 * @property {boolean} [permanently_closed] - Specifies if the place is permanently closed.
 * @property {Array<PlacePhoto>} [photos] - An array of photo objects, each containing a reference to an image.
 * @property {string} [place_id] - A textual identifier that uniquely identifies a place.
 * @property {PlusCode} [plus_code] - An encoded location reference derived from latitude and longitude coordinates.
 * @property {number} [price_level] - The price level of the place, on a scale of 0 to 4.
 * @property {number} [rating] - Contains the place's rating, from 1.0 to 5.0.
 * @property {boolean} [reservable] - Specifies if the place supports reservations.
 * @property {Array<PlaceReview>} [reviews] - A JSON array of up to five reviews.
 * @property {Array<PlaceOpeningHours>} [secondary_opening_hours] - Contains an array of entries for the next seven days.
 * @property {boolean} [serves_beer] - Specifies if the place serves beer.
 * @property {boolean} [serves_breakfast] - Specifies if the place serves breakfast.
 * @property {boolean} [serves_brunch] - Specifies if the place serves brunch.
 * @property {boolean} [serves_dinner] - Specifies if the place serves dinner.
 * @property {boolean} [serves_lunch] - Specifies if the place serves lunch.
 * @property {boolean} [serves_vegetarian_food] - Specifies if the place serves vegetarian food.
 * @property {boolean} [serves_wine] - Specifies if the place serves wine.
 * @property {boolean} [takeout] - Specifies if the business supports takeout.
 * @property {Array<string>} [types] - Contains an array of feature types describing the given result.
 * @property {string} [url] - Contains the URL of the official Google page for this place.
 * @property {number} [user_ratings_total] - The total number of reviews for this place.
 * @property {number} [utc_offset] - Contains the number of minutes this place’s current timezone is offset from UTC.
 * @property {string} [vicinity] - For establishment results only, the vicinity field contains a simplified address for the place.
 * @property {string} [website] - The authoritative website for this place.
 * @property {boolean} [wheelchair_accessible_entrance] - Specifies if the place has an entrance that is wheelchair-accessible.
 */

/**
 * @typedef {Object} AddressComponent
 * @property {string} long_name - The full text description or name of the address component.
 * @property {string} short_name - An abbreviated textual name for the address component.
 * @property {Array<string>} types - An array indicating the type of the address component.
 */

/**
 * @typedef {Object} PlaceEditorialSummary
 * @property {string} [language] - The language of the previous fields.
 * @property {string} [overview] - A medium-length textual summary of the place.
 */

/**
 * @typedef {Object} Geometry
 * @property {LatLngLiteral} location - Describing the location.
 * @property {Bounds} viewport - Describing the viewport.
 */

/**
 * @typedef {Object} LatLngLiteral
 * @property {number} lat - Latitude in decimal degrees.
 * @property {number} lng - Longitude in decimal degrees.
 */

/**
 * @typedef {Object} Bounds
 * @property {LatLngLiteral} northeast - Geographical coordinates.
 * @property {LatLngLiteral} southwest - Geographical coordinates.
 */

/**
 * @typedef {Object} PlaceOpeningHours
 * @property {boolean} [open_now] - A boolean value indicating if the place is open at the current time.
 * @property {Array<PlaceOpeningHoursPeriod>} [periods] - An array of opening periods covering seven days.
 * @property {Array<PlaceSpecialDay>} [special_days] - An array of up to seven entries corresponding to the next seven days.
 * @property {string} [type] - A type string used to identify the type of secondary hours.
 * @property {Array<string>} [weekday_text] - An array of strings describing in human-readable text the hours of the place.
 */

/**
 * @typedef {Object} PlaceOpeningHoursPeriod
 * @property {PlaceOpeningHoursPeriodDetail} open - Contains a pair of day and time objects describing when the place opens.
 * @property {PlaceOpeningHoursPeriodDetail} [close] - May contain a pair of day and time objects describing when the place closes.
 */

/**
 * @typedef {Object} PlaceSpecialDay
 * @property {string} [date] - A date expressed in RFC3339 format in the local timezone for the place.
 * @property {boolean} [exceptional_hours] - True if there are exceptional hours for this day.
 */

/**
 * @typedef {Object} PlaceOpeningHoursPeriodDetail
 * @property {number} day - A number from 0–6, corresponding to the days of the week.
 * @property {string} time - May contain a time of day in 24-hour hhmm format.
 * @property {string} [date] - A date expressed in RFC3339 format in the local timezone for the place.
 * @property {boolean} [truncated] - True if a given period was truncated due to a

/**
 * Extract `LocationData` from a `Place` returned by a get place info or
 * autocomplete query on the google places API.
 *
 * @param {Place} place 
 * @returns {LocationData}
 */
function extractPlaceData(place) {
  const streetNumber = getValueFromAddressComponents(
    place.address_components,
    'street_number'
  );
  const route = getValueFromAddressComponents(
    place.address_components,
    'route'
  );
  const suiteNumber = getValueFromAddressComponents(
    place.address_components,
    'subpremise'
  );
  const subLocality = getValueFromAddressComponents(
    place.address_components,
    'sublocality'
  );
  const cityFromLocality = getValueFromAddressComponents(
    place.address_components,
    'locality'
  );
  const cityFromAdministrativeAreaLevel2 = getValueFromAddressComponents(
    place.address_components,
    'administrative_area_level_2'
  );
  const province = getValueFromAddressComponents(
    place.address_components,
    'administrative_area_level_1'
  );
  const country = getValueFromAddressComponents(
    place.address_components,
    'country'
  );
  const countryComponent = (place.address_components || []).find((c) =>
    c.types?.includes('country')
  );
  const countryCode = countryComponent?.short_name ?? null;
  const zipCode = getValueFromAddressComponents(
    place.address_components,
    'postal_code'
  );

  return {
    lat: place.geometry.location.lat,
    lng: place.geometry.location.lng,
    formattedAddress: place.formatted_address,
    name: place.name,
    streetAddress: getStreetAddress({
      streetNumber,
      route,
      subLocality,
    }),
    suiteNumber,
    city: cityFromLocality ?? cityFromAdministrativeAreaLevel2,
    province,
    country,
    countryCode,
    zipCode,
    placeId: place.place_id,
  };
}

/**
 * Given raw address components, compute a cannonical street address.
 *
 * @param {object} param0
 * @param {string} param0.streetNumber
 * @param {string} param0.route
 * @param {string} param0.subLocality
 * @returns {string}
 */
const getStreetAddress = ({ streetNumber, route, subLocality }) => {
  if (!streetNumber) {
    if (!route) {
      if (!subLocality) {
        return ``;
      } else return subLocality;
    } else {
      if (!subLocality) {
        return route;
      } else {
        return `${route}, ${subLocality}`;
      }
    }
  } else if (streetNumber) {
    if (!route) {
      if (!subLocality) {
        return streetNumber;
      } else {
        return `${streetNumber}, ${subLocality}`;
      }
    } else {
      if (!subLocality) {
        return `${streetNumber}, ${route}`;
      } else {
        return `${streetNumber}, ${route}, ${subLocality}`;
      }
    }
  }
};

module.exports = {
  getValueFromAddressComponents,
  extractPlaceData,
  getStreetAddress,
};
